import type { RuleSet } from 'styled-components';

import styled, { css } from 'styled-components';

import { hideScrollBar } from '../../common/styles';
import { media } from '../styles';
import { Typography } from '~/components/shared/core/typography/Typography';
import { HeadingStyling } from '~/components/unmanaged/styles/BaseStyledStyling';
import { SiteType } from '~/utilities/graphql/codegen';

const GStarCarouselWrapperStyles = css`
  margin-inline: 0 0;
`;

const AafCarouselWrapperStyles = css`
  --product-tile-info-height: 50px;

  margin-inline: 0 0;
`;

const CarouselWrapperStylesConfig: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: GStarCarouselWrapperStyles,
  [SiteType.Wahts]: GStarCarouselWrapperStyles,
  [SiteType.Outlet]: GStarCarouselWrapperStyles,
  [SiteType.Employeeshop]: GStarCarouselWrapperStyles,
  [SiteType.Aaf]: AafCarouselWrapperStyles,
};

const arrowBtnStyles = css`
  position: absolute;
  top: calc(50% - var(--product-tile-info-height, 92px) / 2);
  transform: translateY(-20px);
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  padding: 8px;
  border: none;
  z-index: 10;
  outline: 0;
  cursor: pointer;

  &[data-is-hidden='true'] {
    display: none;
  }

  @media ${({ theme }) => media(theme).lessThan('sm')} {
    display: none;
  }
`;

const GStarArrowButtonStyles = css`
  background-color: ${({ theme }) => theme.colors.background.component.button.solid.dark.rest};
  border-radius: 1px;

  @media (hover: hover) {
    &:hover,
    &:focus {
      background-color: ${({ theme }) => theme.colors.background.component.button.solid.dark.hover};
    }
  }

  g {
    fill: ${({ theme }) => theme.colors.neutral['0']};
  }
`;

const AafArrowButtonStyles = css`
  background-color: ${({ theme }) => theme.colors.content.component.buttons.primary.rest};

  g {
    fill: ${({ theme }) => theme.colors.neutral['0']};
  }
`;

const ArrowButtonStylesConfig: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: GStarArrowButtonStyles,
  [SiteType.Wahts]: GStarArrowButtonStyles,
  [SiteType.Outlet]: GStarArrowButtonStyles,
  [SiteType.Employeeshop]: GStarArrowButtonStyles,
  [SiteType.Aaf]: AafArrowButtonStyles,
};

const GStarCarouselScrollStyles = css`
  --gap: ${({ theme }) => theme.size['1']};

  gap: var(--gap);

  @media ${({ theme }) => media(theme).greaterThan('sm')} {
    --column-width: calc((300 / (3 * var(--max-visible-items, 3) + 1)) * 1%);

    grid-template-columns: repeat(var(--products-count, 0), var(--column-width));
  }

  @media ${({ theme }) => media(theme).greaterThan('lg')} {
    --column-width: calc((300 / (3 * var(--max-visible-items, 5) + 1)) * 1%);
  }

  > * {
    scroll-snap-align: start;

    @media ${({ theme }) => media(theme).greaterThan('tablet')} {
      scroll-snap-align: center;
    }
  }
`;

const AafCarouselScrollStyles = css`
  --gap: ${({ theme }) => theme.size['0-5']};

  gap: var(--gap);

  @media ${({ theme }) => media(theme).greaterThan('md')} {
    --column-width: calc((100% / var(--max-visible-items, 3)) - var(--gap));

    grid-template-columns: repeat(var(--products-count, 0), var(--column-width));
  }

  @media ${({ theme }) => media(theme).greaterThan('lg')} {
    --column-width: calc((100% / var(--max-visible-items, 5)) - var(--gap));
  }

  > * {
    scroll-snap-align: start;

    @media ${({ theme }) => media(theme).greaterThan('tablet')} {
      scroll-snap-align: center;
    }
  }
`;

const CarouselScrollStylesConfig: Record<SiteType, RuleSet> = {
  [SiteType.Gstar]: GStarCarouselScrollStyles,
  [SiteType.Wahts]: GStarCarouselScrollStyles,
  [SiteType.Outlet]: GStarCarouselScrollStyles,
  [SiteType.Employeeshop]: GStarCarouselScrollStyles,
  [SiteType.Aaf]: AafCarouselScrollStyles,
};

const GStarTitleStyles = css`
  letter-spacing: 4%;
`;

const AafTitleStyles = css`
  font-family: var(--font-body);
  line-height: 1;
  letter-spacing: 5%;
`;

const WahtsTitleStyles = css`
  letter-spacing: 15%;
`;

const TitleStylesConfig: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: GStarTitleStyles,
  [SiteType.Wahts]: WahtsTitleStyles,
  [SiteType.Outlet]: GStarTitleStyles,
  [SiteType.Employeeshop]: GStarTitleStyles,
  [SiteType.Aaf]: AafTitleStyles,
};

// Shared with titles rendered outside `CarouselTitle`, such as the Stylitics widget heading.
export const CarouselTitleStyles = css`
  ${({ theme }) => css`
    ${HeadingStyling};

    display: flex;
    justify-content: flex-start;
    padding: ${theme.padding.lg} ${theme.padding.sm} ${theme.padding.md};
    font-size: ${theme.font.size[6]};
    color: ${theme.colors.content.primary};

    @media ${media(theme).greaterThan('sm')} {
      padding-inline: var(--recommendation-margin, 0);
      font-size: ${theme.font.size[7]};
    }

    ${TitleStylesConfig[theme.siteType]}
  `}
`;

export const S = {
  CarouselWrapper: styled.section`
    ${({ theme }) => css`
      position: relative;
      display: none;

      &[data-visible='true'] {
        display: block;
      }

      ${CarouselWrapperStylesConfig[theme.siteType]}
    `}
  `,

  Carousel: styled.div`
    position: relative;
  `,

  CarouselScrollWrapper: styled.div`
    ${hideScrollBar};

    overflow-y: hidden;
    overflow-x: auto;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;
    margin-block: -4px;
    padding-block: 4px;
  `,

  CarouselScroll: styled.ul`
    display: grid;
    padding: 0;
    margin: 0;
    grid-template-columns: repeat(var(--products-count, 0), 43%);

    &[data-rows-on-mobile='Two'] {
      grid-template-columns: repeat(var(--max-visible-items, 2), minmax(0, 1fr));
    }

    ${({ theme }) => CarouselScrollStylesConfig[theme.siteType]}

    > * {
      scroll-snap-align: start;

      @media ${({ theme }) => media(theme).greaterThan('tablet')} {
        scroll-snap-align: center;
      }
    }

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      padding-inline: var(--recommendation-margin, 0);

      &::after {
        content: '';
        width: calc(var(--recommendation-margin, 0) - var(--gap));
        max-width: var(--recommendation-margin, 0);
      }
    }
  `,

  LeftArrow: styled.button`
    ${arrowBtnStyles};
    ${({ theme }) => ArrowButtonStylesConfig[theme.siteType]}

    left: 0;
  `,

  RightArrow: styled.button`
    ${arrowBtnStyles};
    ${({ theme }) => ArrowButtonStylesConfig[theme.siteType]}

    right: 0;
  `,

  TileWrapper: styled.li`
    list-style: none;
    position: relative;
  `,

  Title: styled(Typography)`
    ${CarouselTitleStyles}
  `,
};
